import React, { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { ModalBottomSheet } from './ModalBottomSheet';
import { REPORT_REASON_OPTIONS, type SubmitReportInput } from '../services/reportService';
import type { ReportReasonKey } from '../types/reports';

type Props = {
  visible: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  onSubmit: (input: Omit<SubmitReportInput, 'reporterId'>) => Promise<void>;
  /** targetType + ids + targetAuthorId (reporterId hariç) */
  reportBase: Omit<SubmitReportInput, 'reporterId' | 'reasonKey' | 'description'>;
};

export function ReportContentModal({
  visible,
  title,
  subtitle,
  onClose,
  onSubmit,
  reportBase,
}: Props) {
  const insets = useSafeAreaInsets();
  const [reasonKey, setReasonKey] = useState<ReportReasonKey>('inappropriate');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) {
      setReasonKey('inappropriate');
      setDescription('');
      setError(null);
      setBusy(false);
    }
  }, [visible]);

  const handleSubmit = useCallback(async () => {
    const d = description.trim();
    if (d.length < 1) {
      setError('Kısa bir açıklama yazın.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSubmit({
        ...reportBase,
        reasonKey,
        description: d,
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gönderilemedi.');
    } finally {
      setBusy(false);
    }
  }, [description, onClose, onSubmit, reasonKey, reportBase]);

  return (
    <ModalBottomSheet
      visible={visible}
      onRequestClose={onClose}
      dismissDisabled={busy}
      keyboardAvoiding>
      <View
        className="max-h-[92%] rounded-t-3xl border border-zinc-200 bg-white px-4 pt-3 dark:border-zinc-800 dark:bg-zinc-900"
        style={{ paddingBottom: 16 + insets.bottom }}>
          <View className="mb-3 h-1 w-10 self-center rounded-full bg-zinc-200 dark:bg-zinc-700" />
          <Text className="text-lg font-bold text-zinc-900 dark:text-zinc-50">{title}</Text>
          {subtitle ? (
            <Text className="mt-1 text-sm leading-5 text-zinc-500 dark:text-zinc-400">
              {subtitle}
            </Text>
          ) : null}

          <Text className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
            Sebep
          </Text>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            className="mb-4 max-h-28"
            nestedScrollEnabled>
            <View className="flex-row flex-wrap gap-2 pb-1">
              {REPORT_REASON_OPTIONS.map(opt => {
                const selected = reasonKey === opt.key;
                return (
                  <Pressable
                    key={opt.key}
                    onPress={() => {
                      setReasonKey(opt.key);
                      setError(null);
                    }}
                    className={`rounded-full border px-3 py-2 ${
                      selected
                        ? 'border-zinc-900 bg-zinc-900 dark:border-zinc-100 dark:bg-zinc-100'
                        : 'border-zinc-200 bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800'
                    }`}>
                    <Text
                      className={`text-xs font-semibold ${
                        selected ? 'text-white dark:text-zinc-900' : 'text-zinc-700 dark:text-zinc-200'
                      }`}>
                      {opt.label}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </ScrollView>

          <Text className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
            Açıklama
          </Text>
          <TextInput
            value={description}
            onChangeText={t => {
              setDescription(t);
              setError(null);
            }}
            placeholder="Ne olduğunu kısaca anlatın…"
            placeholderTextColor="#9ca3af"
            multiline
            textAlignVertical="top"
            editable={!busy}
            className="mb-3 min-h-[100px] rounded-2xl border border-zinc-200 bg-zinc-50 px-3 py-3 text-base text-zinc-900 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100"
          />
          {error ? (
            <Text className="mb-2 text-center text-sm text-red-600 dark:text-red-400">{error}</Text>
          ) : null}

          <View className="mt-2 flex-row gap-3">
            <Pressable
              disabled={busy}
              onPress={onClose}
              className="flex-1 items-center rounded-2xl border border-zinc-300 py-3.5 dark:border-zinc-600">
              <Text className="font-semibold text-zinc-700 dark:text-zinc-300">Vazgeç</Text>
            </Pressable>
            <Pressable
              disabled={busy}
              onPress={() => void handleSubmit()}
              className="flex-1 items-center rounded-2xl bg-zinc-900 py-3.5 dark:bg-zinc-50">
              {busy ? (
                <ActivityIndicator color="#ffffff" />
              ) : (
                <Text className="font-semibold text-white dark:text-zinc-950">Şikayeti gönder</Text>
              )}
            </Pressable>
          </View>
        </View>
    </ModalBottomSheet>
  );
}
